import { OG_IMAGE, OG_SITE_NAME, ROBOTS } from "@config/MetaTagsConstants";
import { WEB2_BASE_URL } from "@helper/getEnvVariables";
import { capitalize } from "@helper/stringTransform";
import { dateFormatWithTz, formatDate } from "@helper/useDateFormat";
import { MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ProcessedArticleBody,
  ProcessedAuthorContent,
  ProcessedFullParagraph,
  ProcessedTagsContent,
  RawImageField,
} from "@typings/Opensearch";
import type { GADataProps } from "@typings/PageMetaData";
import type { PageMetaDataValues } from "@typings/PageMetaData";

type ArticleMetaDataProps = {
  article: ProcessedArticleData;
  gsChannels?: string[];
};

export default function ArticleMetaData({
  article,
}: ArticleMetaDataProps): PageMetaDataValues {
  const keywords = getTagsName(article.tags || []);

  const capitalizeSectionName = article.sectionName
    ? capitalize(article.sectionName)
    : article.sectionName;

  const gaData: GADataProps = {
    contentType: "article",
    keyword: keywords,
    title: article.title,
    author: getAuthorsName(article.authors || []),
    pubdate: formatDate(article.createdDate, "yyyy-MM-dd HH:mm:ss"),
    updated_date: formatDate(article.editedDate, "yyyy-MM-dd HH:mm:ss"),
    articleid: article.id,
    cue_articleid: article.id,
    contentcat: 1,
    content_publication_date_utc: article.createdDate,
    content_update_date_utc: article.editedDate,
    level2: article.sectionName.toLowerCase(),
    section: article.sectionName.toLowerCase(),
    chapter1: article.subSection && article.subSection.toLowerCase(),
  };
  let pageUrl = `${article.urlPath}`;
  pageUrl = pageUrl.replace(/^\/+/, "");

  const ogImage = {
    url: article.ogImageUrl || article.imageField?.url,
    width: 480,
    height: 270,
  } as RawImageField;
  const defaultOgImage: string = new URL(OG_IMAGE, WEB2_BASE_URL).toString();

  const metaTitle =
    article.seoFields?.metaTitle && article.seoFields.metaTitle !== ""
      ? article.seoFields.metaTitle
      : `${article.title}, ${capitalizeSectionName}, Tabla`;
  const metaDescription =
    article.seoFields?.metaDescription &&
    article.seoFields.metaDescription.trim() !== ""
      ? article.seoFields.metaDescription
      : getFirstParagraph(article.articleBody);
  const metaRobots =
    article.seoFields?.metaRobots && article.seoFields.metaRobots === "1"
      ? "noindex, nofollow"
      : ROBOTS;

  const canonicalUrl =
    article.seoFields?.canonicalUrl ||
    new URL(`/${pageUrl}`, WEB2_BASE_URL).toString();

  const additionalMetaDescriptors = [
    { tagName: "link", rel: "manifest", href: "/manifest.json" },
  ];

  const metaTags: MetaTagsProps = {
    isArticlePage: true,
    title: metaTitle,
    description: metaDescription,
    keywords: keywords.split(","),
    ogType: "Article",
    slug: `/${pageUrl}`,
    image: ogImage.url || defaultOgImage,
    publishedTime: formatDate(article.createdDate, dateFormatWithTz),
    updatedTime: formatDate(article.editedDate, dateFormatWithTz),
    author: getAuthorsName(article.authors || []),
    section: capitalizeSectionName,
    // gsChannels,
    robots: metaRobots,
    canonicalUrl: canonicalUrl,
    ogTitle: article.ogFields?.ogTitle || metaTitle,
    ogDescription: article.ogFields?.ogDescription || metaDescription,
    ogSiteName: OG_SITE_NAME,
    additionalMetaDescriptors,
  };

  return {
    gaData,
    metaTags,
  };
}

export function getFirstParagraph(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";
  const para = articleBody.find(
    (element) => element.type === "paragraph",
  ) as ProcessedFullParagraph;
  return para.value;
}

export function getTagsName(tags: ProcessedTagsContent[]): string {
  return tags.map((tag) => tag.name).join(",");
}

export function getAuthorsName(authors: ProcessedAuthorContent[]): string {
  return authors.map((author) => author.name).join("|");
}

export const getAuthorNames = (authors: ProcessedAuthorContent[]) => {
  return authors.map((a) => a.name.trim()).join(", ");
};
